/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { useEffect } from "react";
import { KeyBindingService } from "../../keyBinding/KeyBindingService";

/**
 * Sets up global body click event listener when active
 * Usable for closing floating menus and elements.
 *
 * @param keyBindingService Key binding service
 */
export function useKeyBindings(keyBindingService: KeyBindingService): void {
	useEffect(() => {
		const singleKeyWhitelist = [ "Delete", "Escape" ];
		const formKeyBlacklist = [ "Delete", "c", "v", "x", "a" ];

		const handleKeyDown = (ev: KeyboardEvent) => {
			// Ignore events without functional keys (for a better performance)
			if (
				!singleKeyWhitelist.includes(ev.key) &&
				!ev.ctrlKey &&
				!ev.altKey &&
				!ev.shiftKey &&
				!ev.metaKey
			) {
				return;
			}

			if (
				formKeyBlacklist.includes(ev.key) &&
				!!document.activeElement?.closest(
					'button, input, select, textarea, [contenteditable="true" i]'
				)
			) {
				return;
			}

			const virtualEvent = {
				ctrlKey: ev.ctrlKey,
				shiftKey: ev.shiftKey,
				altKey: ev.altKey,
				metaKey: ev.metaKey,
				code: ev.code,
				key: ev.key
			};

			if (keyBindingService.hasBindingForKeyEvent(virtualEvent)) {
				ev.preventDefault();
				ev.stopPropagation();
			}

			keyBindingService.dispatchKeyEvent(virtualEvent);
		};

		window.addEventListener("keydown", handleKeyDown);

		return () => {
			window.removeEventListener("keydown", handleKeyDown);
		};
	}, [ keyBindingService ]);
}
